import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { PortalScope } from '../login/login.component';
import { AuthenticationRequest } from '../models/auth/auth-request.model';
import { LoginStatus } from '../models/auth/login-status.enum';
import { AuthService } from '../services/auth.service';
import { NavigationService } from '../services/navigation.service';
import { EnvironmentService } from 'projects/shared/services/environment.service';
import { Constants } from '../services/constants';
import { Utils } from '../services/utils';
import { Observer } from 'rxjs';
import { PasswordPolicyResponse } from '../models/auth/password-policy-response.model';

@Component({
    selector: 'confirm-account',
    templateUrl: 'confirm-account.component.html',
    styleUrls: ['confirm-account.component.scss'],
    standalone: false
})
export class ConfirmAccountComponent implements OnInit {
  @ViewChild('createAccountForm') createAccountForm: NgForm | undefined;

  userName: string = '';

  password: string = '';
  confirmedPassword: string = '';

  tokenConfirmed: boolean = false;
  accountConfirmed: boolean = false;
  alreadyRegistered: boolean = false;
  errorMessage: string | undefined = undefined;

  spinnerVisible: boolean = false;

  tocAccepted: boolean = false;

  passwordInputState = {
    displayed: false,
  };
  confirmPasswordInputState = {
    displayed: false,
  };

  possibleLogoUrls: string[] = [];

  gotoPortalScope: PortalScope = null;

  protected passwordIsMinimum8Characters: boolean = false;
  protected passwordContainsDigit: boolean = false;
  protected passwordContainsUppercase: boolean = false;
  protected passwordContainsLowercase: boolean = false;
  protected passwordContainsSpecialCharacter: boolean = false;

  passwordPolicy: PasswordPolicyResponse;

  private _token: string = '';
  private _companyGuid: string = '';

  constructor(
    private readonly _authService: AuthService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _navigationService: NavigationService,
    private readonly _environment: EnvironmentService
  ) {
    this.possibleLogoUrls = this._navigationService.possibleLogoUrls;
    this._activatedRoute.queryParams.subscribe((params) => {
      this.userName = params['username'];
      this._token = params['token'];
      this._companyGuid = params['companyGuid'];
    });
    let userScopeFromPath = null;
    const state = this._router.getCurrentNavigation().extras.state;
    if (state && state.gotoPortal) {
      userScopeFromPath = state.goToPortal;
    }
    this.gotoPortalScope = this._environment.scope || userScopeFromPath;
  }

  ngOnInit() {
    const observer: Observer<any> = {
      next: (response: any) => {
        this.tokenConfirmed = true;
        this.errorMessage = undefined;
        this.alreadyRegistered = !!response.isConfirmed;
        if (response.userName) {
          this.userName = response.userName;
        }
      },
      error: (err: any) => {
        this.errorMessage = err.error;
      },
      complete: () => { },
    };

    if (this._companyGuid) {
      this.loadPasswordPolicy(this._companyGuid);
    }

    this.spinnerVisible = true;
    this._authService
      .confirmRegistrationToken(this.userName, this._token, this._companyGuid)
      .subscribe(observer)
      .add(() => {
        this.spinnerVisible = false;
      });
  }

  onGoToSignInClicked = () => {
    this._navigationService.navigateToLogin(this._companyGuid, false, { username: this.userName });
  };

  protected onPasswordStateClicked = (state: { displayed: boolean }) => {
    state.displayed = !state.displayed;
  };

  onPasswordChanged = () => {
    const minLength = this.passwordPolicy?.minimumPasswordLength || 8;
    this.passwordContainsDigit = Utils.containsDigit(this.password);
    this.passwordIsMinimum8Characters = this.password.trim().length >= minLength;
    this.passwordContainsUppercase = Utils.containsUppercase(this.password);
    this.passwordContainsLowercase = Utils.containsLowercase(this.password);
    this.passwordContainsSpecialCharacter = Utils.containsSpecialCharacter(
      this.password
    );
  };

  get isPasswordValid(): boolean {
    if (!this.password) return false;
    if (!this.passwordIsMinimum8Characters) return false;
    const p = this.passwordPolicy;
    if (!p) {
      return this.passwordContainsDigit && this.passwordContainsUppercase
        && this.passwordContainsLowercase && this.passwordContainsSpecialCharacter;
    }
    if (p.requireDigit && !this.passwordContainsDigit) return false;
    if (p.requireUppercase && !this.passwordContainsUppercase) return false;
    if (p.requireLowercase && !this.passwordContainsLowercase) return false;
    if (p.requireSpecialCharacter && !this.passwordContainsSpecialCharacter) return false;
    return true;
  }

  private loadPasswordPolicy(companyGuid: string): void {
    this._authService.getPasswordPolicyByCompanyGuid(companyGuid).subscribe({
      next: (policy) => {
        this.passwordPolicy = policy;
        this.onPasswordChanged();
      }
    });
  }

  onTermsAndConditionsClicked = () => {
    window.open(
      'terms-and-conditions?companyGuid=' + this._companyGuid,
      '_blank'
    );
  };

  onCreatePasswordClicked = () => {
    this.createAccountForm?.form.markAllAsTouched();
    const valid = this.createAccountForm?.form.valid && this.isPasswordValid;
    if (this.tokenConfirmed && valid) {
      this.spinnerVisible = true;
      if (this.accountConfirmed) {
        this.login();
      } else {
        this._authService
          .confirmRegistration(
            this.userName,
            this._token,
            this.password,
            this.confirmedPassword,
            this._companyGuid
          )
          .pipe(
            finalize(() => {
              this.spinnerVisible = false;
            })
          )
          .subscribe(
            (response) => {
              this.accountConfirmed = true;
              this.errorMessage = undefined;
              // The account is resolved from the token, so the username on the invite link can be
              // out of date - sign in as the account the password was actually set on.
              if (response.userName) {
                this.userName = response.userName;
              }

              this.login(response.scope);
            },
            (error) => {
              this.handleCreatePasswordError(error);
            }
          );
      }
    }
  };

  private login = (scope?: PortalScope) => {
    this.spinnerVisible = true;
    if (!this._companyGuid) {
      return;
    }
    const loginRequest: AuthenticationRequest = {
      companyGuid: this._companyGuid,
      username: this.userName,
      password: this.password,
      rememberMe: false,
      scope: scope || this.gotoPortalScope || PortalScope.AgentAndBorrower,
    };
    this._authService.signIn(loginRequest).subscribe(
      (response) => {
        this.spinnerVisible = false;
        if (response.loginStatus === LoginStatus.Error) {
          this.errorMessage = response.errorMessage;
          return;
        } else if (response.loginStatus === LoginStatus.Success) {
          const authData = JSON.parse(
            localStorage.getItem(Constants.authorization.authorizationDataKey)
          );
          if (authData) {
            this.gotoPortalScope = authData.scope;
          }
        } else if (
          response.loginStatus === LoginStatus.ProperScopeSelectionRequired
        ) {
          //this.onRoleSelectionRequired(response.availableCompanies);
          this.gotoPortalScope = PortalScope.Borrower;
          return;
        }
        const portalRoute =
          !this.gotoPortalScope || this.gotoPortalScope == PortalScope.Borrower
            ? 'e-consent'
            : 'agent-portal';
        this._navigationService.navigateToPath(`/${portalRoute}`);
      },
      (error) => {
        this.errorMessage = error.error_descripton;
        this.spinnerVisible = false;
      }
    );
  };

  private handleCreatePasswordError = (error: any) => {
    if (error.error === 'User is already registered. Please log in.') {
      this.login();
    } else {
      this.errorMessage = error.error.message;
    }
  };
}
